import React, { useEffect } from "react";
import type { DiscoveryCelebration } from "../../lib/companionGuide";

interface CelebrationToastProps {
  celebration: DiscoveryCelebration | null;
  onDone: () => void;
}

const CONFETTI_COLORS = [
  "bg-primary/70",
  "bg-amber-400",
  "bg-emerald-400",
  "bg-rose-400",
  "bg-violet-400",
  "bg-sky-400",
  "bg-orange-400",
  "bg-pink-400",
];

const CelebrationToast: React.FC<CelebrationToastProps> = ({
  celebration,
  onDone,
}) => {
  // Auto-dismiss after 5 seconds
  useEffect(() => {
    if (!celebration) return;
    const timer = setTimeout(onDone, 5000);
    return () => clearTimeout(timer);
  }, [celebration, onDone]);

  if (!celebration) return null;

  return (
    <div
      key={celebration.id}
      className="fixed bottom-8 inset-x-0 z-50 flex justify-center pointer-events-none animate-celebration-enter"
    >
      <div className="relative bg-white/95 backdrop-blur-md rounded-full px-6 py-3 shadow-lg shadow-ai/10 border border-ai/10 flex items-center gap-2.5 pointer-events-auto animate-celebration-glow overflow-hidden">
        {/* Shimmer effect */}
        <div className="absolute inset-0 rounded-full bg-[length:200%_100%] bg-gradient-to-r from-transparent via-ai/5 to-transparent animate-shine" />
        {/* Confetti particles */}
        {CONFETTI_COLORS.map((color, i) => (
          <span
            key={i}
            className={`absolute w-1.5 h-1.5 rounded-full ${color} animate-confetti-rise`}
            style={{
              left: `${12 + i * 11}%`,
              bottom: "-4px",
              animationDelay: `${i * 0.12}s`,
            }}
          />
        ))}
        {/* Content */}
        <span className="relative text-2xl select-none animate-bounce-in">
          {celebration.emoji}
        </span>
        <span className="relative text-sm font-medium text-gray-700">
          {celebration.message}
        </span>
      </div>
    </div>
  );
};

export default CelebrationToast;
